import React, { useEffect, useRef, useState, useCallback } from "react";

import * as cornerstone from "@cornerstonejs/core";
import * as csTools3d from "@cornerstonejs/tools";
import {
  ToolGroupManager,
  Enums as csToolsEnums,
  ZoomTool,
  PanTool,
  RectangleROITool,
  EllipticalROITool,
  CircleROITool,
  ArrowAnnotateTool,
  PlanarFreehandROITool,
  StackScrollTool,
  addTool,
  CrosshairsTool,
  WindowLevelTool,
  SynchronizerManager,
  synchronizers,
  annotation // Import annotation directly
} from '@cornerstonejs/tools';
import Events from '@cornerstonejs/tools/enums/Events'
import dicomParser from "dicom-parser";
import * as cornerstoneWADOImageLoader from "@cornerstonejs/dicom-image-loader";
import { loadDicomZip, revokeDicomZipCache, formatLoadProgress } from "../utils/dicomZipLoader";
import { showTip, hideTip } from "../utils/hoverTooltip";
import { colorForAnno, rgbArrayForAnno } from "../utils/annotationShapes";
import { acquireRenderingEngine, releaseRenderingEngine, SHARED_ENGINE_ID } from "../utils/sharedRenderingEngine";
import "@cornerstonejs/streaming-image-volume-loader";

import { Spin } from "antd";
import RadiologyToolbar from "./RadiologyToolbar";
import WindowLevelSyncManager from "../utils/WindowLevelSyncManager";
import RadiologyViewerInfo from "./RadiologyViewerInfo";
import SliceScrollbar from "./SliceScrollbar";

// Import core utilities separately
import * as csCoreUtilities from "@cornerstonejs/core/utilities"; // Correct import for jumpToSlice

// Near the top of the file, after imports but before component declaration
// Global variable to track which viewport user is interacting with
if (typeof window !== 'undefined' && !window.ACTIVE_RADIOLOGY_VIEWPORT) {
  window.ACTIVE_RADIOLOGY_VIEWPORT = null;
}

// Global initialization tracking
let globalInitPromise = null;
const activeInstances = new Set();
const { MouseBindings } = csToolsEnums;

// Define synchronizer ID globally or where accessible
const CAMERA_SYNCHRONIZER_ID = 'CAMERA_SYNCHRONIZER_ID';

// Track slice counts globally to ensure only compatible exams can sync
const sliceRegistry = new Map(); // Map instanceId -> sliceCount
// Track which slice counts are currently being synced
const activeSyncedSliceCounts = new Set();

// Track slice change listeners for synced viewers - organize by slice count
const sliceChangeListeners = new Map(); // Map sliceCount -> Map of instanceId -> callback function

// Add this at the top level to track sync state more reliably
const globalSyncState = {};



const initializeCornerstone = async () => {
  if (!globalInitPromise) {
    globalInitPromise = (async () => {
      try {
        if (!cornerstone.isCornerstoneInitialized()) {
          await cornerstone.init();
        }
        await cornerstoneWADOImageLoader.init({ useWebWorkers: false });
        await csTools3d.init();
      } catch (error) {
        console.error('Failed to initialize CornerstoneJS:', error);
        globalInitPromise = null;
        throw error;
      }
    })();
  }
  return globalInitPromise;
};



const RadiologyViewer = ({ itemId, seriesName, zipUrl, annotations = [], targetAnnotationId = null, showAnnotations = true, onAnnotationCompleted, userRole }) => {
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(null);
  const lastProgressKeyRef = useRef("");
  // Progress fires once per network chunk; only re-render when the label changes.
  const handleLoadProgress = useCallback((p) => {
    const key = `${p.phase}:${p.pct}:${p.loaded}`;
    if (key === lastProgressKeyRef.current) return;
    lastProgressKeyRef.current = key;
    setLoadProgress(p);
  }, []);
  const [error, setError] = useState(null);
  const renderingEngineRef = useRef(null);
  const metadataRef = useRef(new Map());
  const mountedRef = useRef(true);
  const viewportReadyRef = useRef(false);
  const [activeTool, setActiveToolState] = useState(null);
  const [annotationShape, setAnnotationShape] = useState('rectangle');
  const toolGroupIdRef = useRef(null);
  const windowLevelSyncRef = useRef(null);
  const [isSynced, setIsSynced] = useState(false);
  const [syncCompatible, setSyncCompatible] = useState(true);
  const frameOfReferenceUIDRef = useRef(null); // Ref to store the FoR UID
  const annotationEventHandlerRef = useRef(null); // Ref to store the annotation event handler
  const userRoleRef = useRef(userRole); // Add a ref to always track current userRole

  // Update userRoleRef when the prop changes
  useEffect(() => {
    userRoleRef.current = userRole;
  }, [userRole]);
  
  const instanceId = useRef(
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  ).current;
  
  // Initialize global sync state
  useEffect(() => {
    globalSyncState[instanceId] = false;
    return () => {
      delete globalSyncState[instanceId];
    };
  }, [instanceId]);
  
  const viewportId = `viewport-${instanceId}`;
  const renderingEngineId = SHARED_ENGINE_ID;
  const volumeId = `volume-${seriesName}-${instanceId}`;
  const [isMPRView, setIsMPRView] = useState(false);
  const axialViewportId = `axial-${instanceId}`;
  const coronalViewportId = `coronal-${instanceId}`;
  const sagittalViewportId = `sagittal-${instanceId}`;

  const [currentSlice, setCurrentSlice] = useState(0);
  const totalSlicesRef = useRef(0);
  const [voiRange, setVoiRange] = useState({ windowWidth: 0, windowCenter: 0 });
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMip, setIsMip] = useState(false);
  const [slabThickness, setSlabThickness] = useState(5);
  const [slabThicknessMax, setSlabThicknessMax] = useState(100);

  useEffect(() => {
    const handleChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fsEl === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl === el) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  }, []);

  // Define updateSyncedSliceCounters earlier using useCallback
  const updateSyncedSliceCounters = useCallback((sliceIndex) => {
    // Get sync state from the global object for immediate access
    // Fallback to React state if not set in global object
    const currentSyncState = globalSyncState[instanceId] !== undefined
      ? globalSyncState[instanceId]
      : isSynced;

    if (!currentSyncState) {
      return;
    }

    const currentSliceCount = totalSlicesRef.current;
    if (!currentSliceCount || currentSliceCount <= 1) {
      return;
    }

    // Broadcast to every synced viewer, across series of different lengths:
    // same slice count -> identity mapping; different count -> proportional
    // position mapping (approximate anatomical alignment for same-extent scans).
    sliceChangeListeners.forEach((listeners, listenerCount) => {
      const mapped =
        listenerCount === currentSliceCount
          ? sliceIndex
          : Math.round(
              (sliceIndex / Math.max(1, currentSliceCount - 1)) *
                (listenerCount - 1)
            );
      listeners.forEach((callback, listenerId) => {
        if (listenerId !== instanceId) {
          try {
            callback(mapped);
          } catch (error) {
            console.error(`[${instanceId}] Error sending update to ${listenerId}:`, error);
          }
        }
      });
    });
  }, [isSynced, instanceId]); // Keep original dependencies

  // --- Annotation hover tooltip (canvas hit-testing) ---
  // Cornerstone draws annotations on canvas (no DOM nodes), so we hit-test the
  // mouse world position against each annotation's bbox on the current plane.
  useEffect(() => {
    const el = viewerRef.current;
    if (!el || isMPRView) return;
    const handleMove = (e) => {
      try {
        const vp = renderingEngineRef.current?.getViewport(viewportId);
        if (!vp || !viewportReadyRef.current) return hideTip();
        const rect = el.getBoundingClientRect();
        const world = vp.canvasToWorld([e.clientX - rect.left, e.clientY - rect.top]);
        const pad = 2; // mm of slack around the shape
        const hit = (annotations || []).find((a) => {
          if (!Array.isArray(a.points) || a.points.length < 2) return false;
          const xs = a.points.map((p) => p[0]);
          const ys = a.points.map((p) => p[1]);
          return (
            Math.abs(world[2] - a.points[0][2]) < 1.0 && // same slice plane
            world[0] >= Math.min(...xs) - pad && world[0] <= Math.max(...xs) + pad &&
            world[1] >= Math.min(...ys) - pad && world[1] <= Math.max(...ys) + pad
          );
        });
        const tip = hit && [hit.name, hit.description].filter(Boolean).join(" — ");
        if (tip) showTip(tip, e.clientX, e.clientY);
        else hideTip();
      } catch (_) {
        hideTip();
      }
    };
    const leave = () => hideTip();
    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", leave);
    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", leave);
      hideTip();
    };
  }, [annotations, isMPRView, viewportId]);

  // --- Annotation Navigation Effect ---
  useEffect(() => {
    if (isMPRView) {
      console.warn(`[RadiologyViewer ${instanceId}] Annotation navigation currently not supported in MPR view.`);
      return;
    }

    if (!targetAnnotationId || !viewportReadyRef.current || !renderingEngineRef.current) return;

    const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);

    if (targetAnno && typeof targetAnno.sliceIndex === 'number') {
      const targetSliceIndex = targetAnno.sliceIndex;

      const viewport = renderingEngineRef.current.getViewport(viewportId);
      const volume = cornerstone.cache.getVolume(volumeId);

      if (viewport && volume && viewport.element) {
        const numSlices = volume.dimensions[2];
        
        // IMPORTANT: The annotation sliceIndex is now assumed to be the DISPLAYED slice number.
        // We need to calculate the internal 0-based index for Cornerstone.
        // With X-axis inverted (viewPlaneNormal [0, 0, -1]), we need to adjust the calculation
        // to ensure "Go to" works correctly with the inverted orientation
        
        // Old calculation before X-axis inversion:
        // const internalIndex = numSlices - targetSliceIndex; 
        // const cornerstoneIndex = Math.max(0, Math.min(internalIndex - 1, numSlices - 1));
        
        // New calculation for X-axis inversion - directly use targetSliceIndex
        // as the cornerstoneIndex, with boundary checking
        const cornerstoneIndex = Math.max(0, Math.min(targetSliceIndex, numSlices - 1));
        
        try {
           // Use jumpToSlice from @cornerstonejs/core/utilities
           csCoreUtilities.jumpToSlice(viewport.element, { imageIndex: cornerstoneIndex })
             .then(() => {
                
                // Update the displayed slice number based on the actual index jumped to
                // Use the same logic as handleSliceChange for consistency
                const { currentStepIndex: actualJumpedIndex, numScrollSteps: totalSteps } = 
                    cornerstone.utilities.getVolumeViewportScrollInfo(viewport, volumeId);
                
                // With X-axis inverted, we should use the actualJumpedIndex directly
                // Old calculation: const displaySliceNum = totalSteps - actualJumpedIndex;
                const displaySliceNum = actualJumpedIndex;
                setCurrentSlice(displaySliceNum);

                if (isSynced) {
                    // updateSyncedSliceCounters expects the display index
                    updateSyncedSliceCounters(displaySliceNum);
                }
                
                // Force the annotation to be visible by setting its highlight state
                try {
                  // Get the annotation state - use getAllAnnotations() and filter
                  const allAnnotations = annotation.state.getAllAnnotations();
                  
                  // Find our target annotation
                  const targetCornerstoneAnno = allAnnotations.find(anno => 
                    anno.annotationUID === targetAnnotationId
                  );
                  
                  if (targetCornerstoneAnno) {
                    // Update it to be highlighted
                    targetCornerstoneAnno.highlighted = true;
                  } else {
                    console.warn(`[RadiologyViewer ${instanceId}] Could not find annotation ${targetAnnotationId} to highlight`);
                  }
                } catch (err) {
                  console.error(`[RadiologyViewer ${instanceId}] Error highlighting annotation:`, err);
                }
                 
                viewport.render();
             })
             .catch(error => {
                 console.error(`[RadiologyViewer ${instanceId}] Error using core jumpToSlice for index ${cornerstoneIndex}:`, error);
             });

        } catch (error) {
            console.error(`[RadiologyViewer ${instanceId}] General error navigating to slice ${cornerstoneIndex}:`, error);
        }
      } else {
        console.warn(`[RadiologyViewer ${instanceId}] Viewport, Volume, or Element not ready for slice navigation.`);
      }
    } else if (targetAnno) {
      console.warn(`[RadiologyViewer ${instanceId}] Target annotation found, but missing valid sliceIndex.`, targetAnno);
    }

  }, [targetAnnotationId, annotations, frameOfReferenceUIDRef.current, viewportReadyRef.current, isMPRView, instanceId, viewportId, volumeId, isSynced, updateSyncedSliceCounters, itemId]);

  const handleVOIUpdate = (viewportId, voiRange) => {
    
    // Optionally, update the state to display the VOI range
    setVoiRange({
      windowWidth: voiRange.windowWidth,
      windowCenter: voiRange.windowCenter,
    });
  };
  

  // --- Register Slice Update Listener Effect ---
  useEffect(() => {
    if (isSynced && totalSlicesRef.current > 1) {
      const currentSliceCount = totalSlicesRef.current;
      
      if (!sliceChangeListeners.has(currentSliceCount)) {
        sliceChangeListeners.set(currentSliceCount, new Map());
      }
      
      // Store a function that updates this instance's slice index
      const updateFunction = (newSliceIndex) => {
        setCurrentSlice(newSliceIndex);
        
        // If viewport is ready, directly update the slice position for smoother sync
        if (viewportReadyRef.current && renderingEngineRef.current) {
          try {
            const viewport = renderingEngineRef.current.getViewport(viewportId);
            if (viewport) {
              const volume = cornerstone.cache.getVolume(volumeId);
              if (volume) {
                // With X-axis inverted, use newSliceIndex directly
                // Old calculation: const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                const sliceIndex = newSliceIndex;
                const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                
                if (typeof viewport.setImageIdIndex === 'function') {
                  viewport.setImageIdIndex(imageIndex);
                } else if (typeof viewport.setDisplaySet === 'function') {
                  viewport.setDisplaySet(volumeId, { imageIndex });
                }
                viewport.render();
              }
            }
          } catch (error) {
            console.warn(`[${instanceId}] Failed to directly update slice position:`, error);
          }
        }
      };
      
      // Store this function in the listeners map
      sliceChangeListeners.get(currentSliceCount).set(instanceId, updateFunction);
    }

    return () => {
      // Cleanup listener when component unmounts or when sync is disabled
      if (totalSlicesRef.current > 0) {  // Guard against invalid slice count
        const currentSliceCount = totalSlicesRef.current;
        const listeners = sliceChangeListeners.get(currentSliceCount);
        if (listeners) {
          listeners.delete(instanceId);
          if (listeners.size === 0) {
            sliceChangeListeners.delete(currentSliceCount);
          }
        }
      }
    };
  }, [isSynced, instanceId, viewportId, volumeId]); // This effect does NOT depend on updateSyncedSliceCounters

  // Update the handleSliceChange function
  const handleSliceChange = useCallback((evt) => {
    const renderingEngine = renderingEngineRef.current;
    if (!renderingEngine) {
      console.warn(`[${instanceId}] handleSliceChange: renderingEngine is null`);
      return;
    }
    
    const viewport = renderingEngine.getViewport(viewportId);
    if (!viewport) {
      console.warn(`[${instanceId}] handleSliceChange: viewport not found`);
      return;
    }
    
    const element = viewport.element;
    if (!element) {
      console.warn(`[${instanceId}] handleSliceChange: viewport element not found`);
      return;
    }

    const enabledElement = cornerstone.getEnabledElement(element);
    if (!enabledElement) {
      console.warn(`[${instanceId}] handleSliceChange: enabledElement not found`);
      return;
    }

    const { viewport: _viewport } = enabledElement;
    const { currentStepIndex, numScrollSteps } = cornerstone.utilities.getVolumeViewportScrollInfo(
      _viewport,
      volumeId
    );

    // IMPORTANT: Cornerstone3D uses 0-based indexing internally where:
    // - currentStepIndex = 0 means we're at the first slice
    // - numScrollSteps - 1 is the last slice
    
    // The internal Cornerstone slice index (0-based)
    const cornerstoneSliceIndex = currentStepIndex;
    
    // With X-axis inverted (viewPlaneNormal [0, 0, -1]), we use the currentStepIndex directly
    // Old calculation before X-axis inversion:
    // const displaySliceIndex = numScrollSteps - currentStepIndex;
    const displaySliceIndex = currentStepIndex;
    
    // Update local state with the display index
    setCurrentSlice(displaySliceIndex);
    
    // Get sync state from the global object for immediate access
    // Fallback to React state if not set in global object
    const currentSyncState = globalSyncState[instanceId] !== undefined 
      ? globalSyncState[instanceId] 
      : isSynced;
      
    
    // Then broadcast to other synced viewers using the display index
    if (currentSyncState) {
      updateSyncedSliceCounters(displaySliceIndex);
    }
  }, [viewportId, volumeId, updateSyncedSliceCounters, isSynced, instanceId]);
  
  

  // Function to check if this instance can sync with the active sync group
  const checkSyncCompatibility = useCallback(() => {
    // Any multi-slice volume can sync — cross-series scrolling is mapped
    // proportionally in updateSyncedSliceCounters.
    return totalSlicesRef.current > 1;
  }, []);

  // Update compatibility whenever active sync groups change
  useEffect(() => {
    const updateCompatibility = () => {
      if (totalSlicesRef.current <= 1 || isMPRView) {
        setSyncCompatible(false);
      } else {
        setSyncCompatible(checkSyncCompatibility());
      }
      
      // If synced, check if our listener is still registered and re-register if necessary
      if (isSynced && totalSlicesRef.current > 1) {
        const currentSliceCount = totalSlicesRef.current;
        const listeners = sliceChangeListeners.get(currentSliceCount);
        
        if (!listeners || !listeners.has(instanceId)) {
          
          if (!sliceChangeListeners.has(currentSliceCount)) {
            sliceChangeListeners.set(currentSliceCount, new Map());
          }
          
          // Create a dedicated update function for this instance
          const syncUpdateFunction = (newSliceIndex) => {
            setCurrentSlice(newSliceIndex);
            
            // If viewport is ready, directly update the slice position for smoother sync
            if (viewportReadyRef.current && renderingEngineRef.current) {
              try {
                const viewport = renderingEngineRef.current.getViewport(viewportId);
                if (viewport) {
                  const volume = cornerstone.cache.getVolume(volumeId);
                  if (volume) {
                    // With X-axis inverted, use newSliceIndex directly
                    // Old calculation: const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                    const sliceIndex = newSliceIndex;
                    const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                    
                    if (typeof viewport.setImageIdIndex === 'function') {
                      viewport.setImageIdIndex(imageIndex);
                    } else if (typeof viewport.setDisplaySet === 'function') {
                      viewport.setDisplaySet(volumeId, { imageIndex });
                    }
                    viewport.render();
                  }
                }
              } catch (error) {
                console.warn(`[${instanceId}] Failed to directly update slice position:`, error);
              }
            }
          };
          
          sliceChangeListeners.get(currentSliceCount).set(instanceId, syncUpdateFunction);
          activeSyncedSliceCounts.add(currentSliceCount);
        }
      }
    };

    // Initial check
    updateCompatibility();
    
    // Listen for changes to sync groups
    const handleSyncGroupChange = () => updateCompatibility();
    window.addEventListener('syncGroupsChanged', handleSyncGroupChange);
    
    return () => {
      window.removeEventListener('syncGroupsChanged', handleSyncGroupChange);
    };
  }, [checkSyncCompatibility, isMPRView, isSynced, instanceId, totalSlicesRef, viewportId, volumeId]);

  useEffect(() => {
    mountedRef.current = true;
    activeInstances.add(instanceId);
    
    return () => {
      mountedRef.current = false;
      
      // If this instance was synced, update the active sync groups
      if (isSynced) {
        // Count how many instances remain with this slice count
        let sameSliceCountRemaining = 0;
        for (const otherId of activeInstances) {
          if (otherId !== instanceId && sliceRegistry.get(otherId) === totalSlicesRef.current) {
            sameSliceCountRemaining++;
          }
        }
        
        // If this was the last or second-to-last instance with this slice count,
        // remove from active sync groups (need at least 2 for meaningful sync)
        if (sameSliceCountRemaining < 1) {
          activeSyncedSliceCounts.delete(totalSlicesRef.current);
          // Notify all instances that sync groups changed
          window.dispatchEvent(new Event('syncGroupsChanged'));
        }
      }
      
      activeInstances.delete(instanceId);
      sliceRegistry.delete(instanceId);
    };
  }, [instanceId, isSynced, totalSlicesRef]);

  // Function to get or create the stack scroll synchronizer
  const getOrCreateCameraSynchronizer = () => {
    let synchronizer = SynchronizerManager.getSynchronizer(
      CAMERA_SYNCHRONIZER_ID
    );

    if (!synchronizer) {
      synchronizer = synchronizers.createCameraPositionSynchronizer(
        CAMERA_SYNCHRONIZER_ID
      );
    }
    return synchronizer;
  };

  // Toggle sync state and update synchronizer
  const handleSyncToggle = () => {
    // Prevent toggling if disabled (e.g., single slice) or in MPR view
    if (isMPRView || totalSlicesRef.current <= 1) {
        return;
    }

    // If trying to enable sync but not compatible with current sync groups
    if (!isSynced && !syncCompatible) {
        return;
    }

    const currentSliceCount = totalSlicesRef.current;


    // Store the next sync state for event dispatching
    const nextSyncState = !isSynced;
    
    // Prepare a custom event to synchronize all viewers with the same slice count
    const syncEvent = new CustomEvent('syncStateChanged', {
      detail: {
        sliceCount: currentSliceCount,
        enabled: nextSyncState,
        sourceInstanceId: instanceId
      }
    });

    // Update local state first - use our custom function that also updates global state
    setSyncedState(nextSyncState);
    
    // Apply synchronizer changes
    const synchronizer = getOrCreateCameraSynchronizer();
    const currentViewportIds = isMPRView
      ? [axialViewportId, coronalViewportId, sagittalViewportId]
      : [viewportId];

    currentViewportIds.forEach(vpId => {
      try {
        if (!renderingEngineRef.current) {
          console.warn(`RenderingEngine not available for viewport ${vpId}`);
          return;
        }
        const viewport = renderingEngineRef.current.getViewport(vpId);
        if (!viewport) {
          console.warn(`Viewport ${vpId} not found`);
          return;
        }
        if (typeof viewport.setVolumes !== 'function') {
          console.warn(`Viewport ${vpId} is not a volume viewport`);
          return;
        }

        if (nextSyncState) {
          synchronizer.add({ renderingEngineId, viewportId: vpId });
          
          // Add this slice count to active sync groups
          activeSyncedSliceCounts.add(totalSlicesRef.current);
          
          // Register slice change listener for this instance
          if (!sliceChangeListeners.has(currentSliceCount)) {
            sliceChangeListeners.set(currentSliceCount, new Map());
          }
          
          // Create a dedicated update function for this instance
          const syncUpdateFunction = (newSliceIndex) => {
            // Update the state for UI display
            setCurrentSlice(newSliceIndex);
            
            // Direct viewport update for smoother sync
            if (viewportReadyRef.current && renderingEngineRef.current) {
              try {
                const viewport = renderingEngineRef.current.getViewport(vpId);
                if (viewport) {
                  const volume = cornerstone.cache.getVolume(volumeId);
                  if (volume) {
                    // With X-axis inverted, use newSliceIndex directly
                    // Old calculation: const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                    const sliceIndex = newSliceIndex;
                    const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                    
                    if (typeof viewport.setImageIdIndex === 'function') {
                      viewport.setImageIdIndex(imageIndex);
                    } else if (typeof viewport.setDisplaySet === 'function') {
                      viewport.setDisplaySet(volumeId, { imageIndex });
                    }
                    viewport.render();
                  }
                }
              } catch (error) {
                console.warn(`[${instanceId}] Failed to update slice in sync toggle:`, error);
              }
            }
          };
          
          sliceChangeListeners.get(currentSliceCount).set(instanceId, syncUpdateFunction);
        } else {
          try {
            synchronizer.remove({ renderingEngineId, viewportId: vpId });
          } catch (error) {
            console.warn(`[${instanceId}] Error removing from synchronizer:`, error);
          }
          
          // Remove from listeners
          const listeners = sliceChangeListeners.get(currentSliceCount);
          if (listeners && listeners.has(instanceId)) {
            listeners.delete(instanceId);
          }
          
          // Check if this was the last or second-to-last instance with this slice count
          let sameSliceCountRemaining = 0;
          for (const otherId of activeInstances) {
            if (otherId !== instanceId && 
                sliceRegistry.get(otherId) === totalSlicesRef.current && 
                globalSyncState[otherId]) {
              sameSliceCountRemaining++;
            }
          }
          
          // If this was the last or second-to-last instance with this slice count,
          // remove from active sync groups (need at least 2 for meaningful sync)
          if (sameSliceCountRemaining < 1) {
            activeSyncedSliceCounts.delete(totalSlicesRef.current);
          }
        }
      } catch (error) {
        console.error(`[${instanceId}] Error updating synchronizer:`, error);
      }
    });
    
    // Dispatch the event to notify other viewers with the same slice count
    window.dispatchEvent(syncEvent);
    
    // Notify that sync groups have changed
    window.dispatchEvent(new Event('syncGroupsChanged'));
  };

  const initializeTools = () => {
    // Add tools to Cornerstone3D
    if (!csTools3d.ToolGroupManager.getToolGroup(`toolGroup-${instanceId}`)) {
      addTool(PanTool);
      addTool(ZoomTool);
      addTool(RectangleROITool);
      addTool(EllipticalROITool);
      addTool(CircleROITool);
      addTool(ArrowAnnotateTool);
      addTool(PlanarFreehandROITool);
      addTool(StackScrollTool);
      addTool(CrosshairsTool);
      addTool(WindowLevelTool);
    }

    const toolGroupId = `toolGroup-${instanceId}`;

    // Check if the tool group already exists
    let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    if (toolGroup) {
      // If tool group exists, destroy it first
      ToolGroupManager.destroyToolGroup(toolGroupId);
    }

    // Create a new tool group
    toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

    if (!toolGroup) {
      console.error(`Failed to create tool group: ${toolGroupId}`);
      return null;
    }

    // Add tools to the tool group
    toolGroup.addTool(PanTool.toolName);
    toolGroup.addTool(ZoomTool.toolName);
    
    // Add Rectangle ROI with configuration for logging
    toolGroup.addTool(RectangleROITool.toolName, {
      configuration: {
        eventListeners: {
          annotationCompleted: (evt) => {
            const { annotation } = evt.detail;
            
            // Check which viewport is active when the annotation is completed
            const activeViewport = window.ACTIVE_RADIOLOGY_VIEWPORT;

            
            // If we have an active viewport and it doesn't match this one, ignore the event
            if (activeViewport && (activeViewport.itemId !== itemId || activeViewport.instanceId !== instanceId)) {
              return;
            }
            
            // Add viewportId and sourceItemId to the annotation metadata for filtering
            if (annotation && annotation.metadata) {
              annotation.metadata.viewportId = viewportId;
              annotation.metadata.sourceItemId = itemId; 
              annotation.metadata.instanceId = instanceId;
            } else if (annotation) {
              // If metadata doesn't exist, create it
              annotation.metadata = {
                viewportId: viewportId,
                sourceItemId: itemId,
                instanceId: instanceId
              };
            }
            
            
            
            const viewport = renderingEngineRef.current?.getViewport(viewportId);
            if (viewport) {
              const { currentStepIndex, numScrollSteps } = cornerstone.utilities.getVolumeViewportScrollInfo(
                viewport,
                volumeId
              );
              
              // Get the image IDs to find the current slice
              const allImageIds = Array.from(metadataRef.current.keys());
              const imageId = currentStepIndex < allImageIds.length 
                ? allImageIds[currentStepIndex] 
                : null;
              
              if (!imageId) {
                console.warn(`[RadiologyViewer ${instanceId}] Could not find imageId for current slice ${currentStepIndex}`);
                return;
              }
              
              // Get proper world Z from image plane module
              const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId);
              
              const worldZ = imagePlaneModule?.imagePositionPatient?.[2] || 0;

              // Call the parent callback if available
              if (typeof onAnnotationCompleted === 'function') {
                const formatForCase = {
                  id: `rectangle-${Date.now()}`,
                  name: 'Rectangle ROI',
                  description: 'Added annotation',
                  toolName: 'RectangleROI',
                  sliceIndex: currentStepIndex,
                  // Trust the handle points' own world Z (see note in the main
                  // ANNOTATION_COMPLETED handler) — fall back to the lookup.
                  points: [
                    [
                      annotation?.data?.handles?.points[0][0],
                      annotation?.data?.handles?.points[0][1],
                      Number.isFinite(annotation?.data?.handles?.points[0]?.[2])
                        ? annotation.data.handles.points[0][2]
                        : worldZ,
                    ],
                    [
                      annotation?.data?.handles?.points[3][0],
                      annotation?.data?.handles?.points[3][1],
                      Number.isFinite(annotation?.data?.handles?.points[3]?.[2])
                        ? annotation.data.handles.points[3][2]
                        : worldZ,
                    ],
                  ],
                  sourceItemId: itemId,
                  viewportId: viewportId,
                  instanceId: instanceId,
                };
                onAnnotationCompleted(itemId, {...formatForCase, userRole: userRoleRef.current});
              }
            }
          }
        },
        // Additional configuration for better visibility
        lineWidth: 3,
        color: [255, 0, 0]
      }
    });
    
    toolGroup.addTool(EllipticalROITool.toolName, { configuration: { lineWidth: 3, color: [255, 0, 0] } });
    toolGroup.addTool(CircleROITool.toolName, { configuration: { lineWidth: 3, color: [255, 0, 0] } });
    // Suppress Cornerstone's native arrow label box on both creation
    // (getTextCallback) and double-click edit (changeTextCallback) so arrows
    // flow only through the shared "Nouvelle annotation" modal like every other
    // shape — never their own separate dialog.
    toolGroup.addTool(ArrowAnnotateTool.toolName, {
      configuration: {
        lineWidth: 3,
        color: [255, 0, 0],
        getTextCallback: (cb) => cb(''),
        changeTextCallback: (...args) => {
          const cb = args[args.length - 1];
          if (typeof cb === 'function') cb('');
        },
      },
    });
    toolGroup.addTool(PlanarFreehandROITool.toolName, { configuration: { lineWidth: 3, color: [255, 0, 0] } });

    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.addTool(WindowLevelTool.toolName);

    toolGroup.setToolDisabled(PanTool.toolName);
    toolGroup.setToolDisabled(ZoomTool.toolName);

    // First set RectangleROI as active to properly render pre-drawn annotations
    toolGroup.setToolActive(RectangleROITool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });

    // Then set it to enabled state (not disabled) so it renders annotations without requiring interaction
    toolGroup.setToolEnabled(RectangleROITool.toolName);
    toolGroup.setToolEnabled(EllipticalROITool.toolName);
    toolGroup.setToolEnabled(CircleROITool.toolName);
    toolGroup.setToolEnabled(ArrowAnnotateTool.toolName);
    toolGroup.setToolEnabled(PlanarFreehandROITool.toolName);

    toolGroup.setToolActive(StackScrollTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Wheel }],
    });
    toolGroup.setToolActive(WindowLevelTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }], // Left mouse drag for windowing
    });
    setActiveToolState(WindowLevelTool.toolName);

    toolGroup.addViewport(viewportId, renderingEngineId);

    // Listen for the annotation added event
    const eventTarget = cornerstone.eventTarget;
    if (eventTarget) {
      // Create a named handler function that we can reference for removal
      const handleAnnotationCompleted = (evt) => {
      
        // CRITICAL FIX: Check if this event was meant for this specific viewer
        // Check if we have an active viewport that doesn't match this instance
        if (window.ACTIVE_RADIOLOGY_VIEWPORT && 
            window.ACTIVE_RADIOLOGY_VIEWPORT.instanceId !== instanceId) {
          return;
        }
        
        // Always explicitly update annotation metadata with this viewer's identity
        // This ensures we don't get cross-contamination between viewers
        if (evt.detail?.annotation) {
          if (!evt.detail.annotation.metadata) {
            evt.detail.annotation.metadata = {};
          }
          evt.detail.annotation.metadata.viewportId = viewportId;
          evt.detail.annotation.metadata.sourceItemId = itemId;
          evt.detail.annotation.metadata.instanceId = instanceId;
        }
        
        // Check if this annotation was created in THIS viewport
        const annotationViewportId = evt.detail?.annotation?.metadata?.viewportId;
        
        // Check if the annotation has a sourceItemId and if it matches this viewer's itemId
        const annotationSourceItemId = evt.detail?.annotation?.metadata?.sourceItemId;
        
        // Also check instanceId - this is a more reliable way to filter annotations
        const annotationInstanceId = evt.detail?.annotation?.metadata?.instanceId;
        
        
        
        // First check instanceId match - this is the most reliable check
        if (annotationInstanceId && annotationInstanceId !== instanceId) {
          return; // Skip processing annotations from other instances
        }
        
        // Fall back to other checks if instanceId not available
        if (annotationSourceItemId && annotationSourceItemId !== itemId) {
          return; // Skip processing annotations from other items
        }
        
        if (annotationViewportId && annotationViewportId !== viewportId) {
          return; // Skip processing annotations from other viewports
        }
        
        
        const SUPPORTED_ANNOTATION_TOOLS = [
          'RectangleROI', 'EllipticalROI', 'CircleROI', 'ArrowAnnotate', 'PlanarFreehandROI',
        ];
        const evtToolName = evt.detail?.annotation?.metadata?.toolName;
        if (SUPPORTED_ANNOTATION_TOOLS.includes(evtToolName)) {
          const anno = evt.detail.annotation;

          // Add or update source item ID and instanceId to the annotation metadata
          if (anno.metadata) {
            // Always set sourceItemId and instanceId to ensure they match this viewer
            anno.metadata.sourceItemId = itemId;
            anno.metadata.instanceId = instanceId;
          }


          // Get current slice info
          const viewport = renderingEngineRef.current?.getViewport(viewportId);
          if (viewport) {
            const sliceInfo = cornerstone.utilities.getVolumeViewportScrollInfo(
              viewport, volumeId
            );
            const currentSliceIndex = sliceInfo.currentStepIndex;

            // Get the image IDs to find the current slice
            const allImageIds = Array.from(metadataRef.current.keys());
            const imageId = currentSliceIndex < allImageIds.length
              ? allImageIds[currentSliceIndex]
              : null;

            if (!imageId) {
              console.warn(`[RadiologyViewer ${instanceId}] Could not find imageId for current slice ${currentSliceIndex}`);
              return;
            }

            // Get proper world Z from image plane module
            const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId);

            const worldZ = imagePlaneModule?.imagePositionPatient?.[2] || 0;

            // The handle points from Cornerstone already carry the true world Z
            // of the drawn plane — trust them. The index→imageId lookup above is
            // only a fallback: metadataRef is ordered by instanceNumber, which
            // can disagree with the volume's spatial slice order on some series
            // (this was putting annotations on the wrong slice).
            const sourceHandlePoints = anno?.data?.handles?.points || [];
            const zOf = (p) => (Number.isFinite(p?.[2]) ? p[2] : worldZ);
            let savedPoints;
            if (evtToolName === 'RectangleROI') {
              savedPoints = [
                [sourceHandlePoints[0][0], sourceHandlePoints[0][1], zOf(sourceHandlePoints[0])],
                [sourceHandlePoints[3][0], sourceHandlePoints[3][1], zOf(sourceHandlePoints[3])],
              ];
            } else {
              savedPoints = sourceHandlePoints.map(p => [p[0], p[1], zOf(p)]);
            }

            const friendlyName = {
              RectangleROI: 'Rectangle ROI',
              EllipticalROI: 'Ellipse ROI',
              CircleROI: 'Circle ROI',
              ArrowAnnotate: 'Arrow',
              PlanarFreehandROI: 'Freehand ROI',
            }[evtToolName] || evtToolName;

            // Pass data to parent component if callback exists
            if (typeof onAnnotationCompleted === 'function') {
              const formatForCase = {
                id: `${evtToolName.toLowerCase()}-${Date.now()}`,
                name: friendlyName,
                description: 'Added annotation',
                toolName: evtToolName,
                sliceIndex: currentSliceIndex,
                points: savedPoints,
                sourceItemId: itemId,
                viewportId: viewportId,
                instanceId: instanceId,
              };

              onAnnotationCompleted(itemId, {...formatForCase, userRole: userRoleRef.current});
            }
          }
        }
      };
      
      // Store the handler in the ref for cleanup
      annotationEventHandlerRef.current = handleAnnotationCompleted;
      
      // Set up a direct listener on the cornerstone event target
      eventTarget.addEventListener(
        Events.ANNOTATION_COMPLETED,
        handleAnnotationCompleted
      );
      
    }

    // Set up global annotation configuration to make them always visible
    annotation.config.style.setViewportToolStyles(viewportId, {
      annotationStyle: {
        rectangleRoi: {
          color: 'rgb(0, 255, 0)',
          colorHighlighted: 'rgb(0, 255, 255)',
          lineWidth: 3,
          lineDash: [],
          isVisible: true,
          textBoxVisibility: false
        }
      },
      toolGroupId: toolGroupId
    });

    // Store the tool group ID reference
    toolGroupIdRef.current = toolGroupId;

    return toolGroupId;
  };
  
  // Set up another method for removing event listeners
  const cleanupEventListeners = () => {
    const eventTarget = cornerstone.eventTarget;
    if (eventTarget && annotationEventHandlerRef.current) {
      try {
        // Remove the ANNOTATION_COMPLETED event listener with the specific handler
        eventTarget.removeEventListener(Events.ANNOTATION_COMPLETED, annotationEventHandlerRef.current);        // Clear the reference
        annotationEventHandlerRef.current = null;
      } catch (error) {
        console.warn(`[RadiologyViewer ${instanceId}] Error removing event listeners:`, error);
      }
    }
  };
  
  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupEventListeners();
    };
  }, [instanceId]);

  const loadMetadata = async () => {
    const baseUrl = process.env.PUBLIC_URL || "";
    const { files, blobUrls } = await loadDicomZip(seriesName, baseUrl, handleLoadProgress, zipUrl);

    const entries = [];
    let firstFoRUID = null;

    for (const [filename, arrayBuffer] of files.entries()) {
      try {
        const byteArray = new Uint8Array(arrayBuffer);
        const dataSet = dicomParser.parseDicom(byteArray);

        const imageId = `wadouri:${blobUrls.get(filename)}`;
        const foRUID = dataSet.string("x00200052");

        if (!firstFoRUID && foRUID) {
          firstFoRUID = foRUID;
        } else if (foRUID && firstFoRUID && foRUID !== firstFoRUID) {
          console.warn(`[RadiologyViewer ${instanceId}] Inconsistent FrameOfReferenceUID found in series (${foRUID} vs ${firstFoRUID})`);
        }

        entries.push({
          imageId,
          instanceNumber: dataSet.intString("x00200013") || 0,
          sliceLocation: dataSet.floatString("x00201041") || 0,
          meta: {
            imageOrientationPatient:
              dataSet.string("x00200037")?.split("\\").map(parseFloat) || [1, 0, 0, 0, 1, 0],
            imagePositionPatient:
              dataSet.string("x00200032")?.split("\\").map(parseFloat) || [0, 0, 0],
            pixelSpacing:
              dataSet.string("x00280030")?.split("\\").map(parseFloat) || [1.0, 1.0],
            rows: dataSet.uint16("x00280010") || 512,
            columns: dataSet.uint16("x00280011") || 512,
            pixelRepresentation: dataSet.uint16("x00280103") || 0,
            bitsAllocated: dataSet.uint16("x00280100") || 16,
            bitsStored: dataSet.uint16("x00280101") || 12,
            highBit: dataSet.uint16("x00280102") || 11,
            photometricInterpretation:
              dataSet.string("x00280004") || "MONOCHROME2",
            windowWidth: dataSet.floatString("x00281051"),
            windowCenter: dataSet.floatString("x00281050"),
            frameOfReferenceUID: foRUID,
          },
        });
      } catch (error) {
        console.error(`Error loading metadata for file ${filename}:`, error);
        throw new Error(`Failed to load metadata for file ${filename}`);
      }
    }

    entries.sort((a, b) => {
      if (a.instanceNumber !== b.instanceNumber) return a.instanceNumber - b.instanceNumber;
      return a.sliceLocation - b.sliceLocation;
    });

    const metadata = new Map(entries.map((e) => [e.imageId, e.meta]));
    frameOfReferenceUIDRef.current = firstFoRUID || `for-${seriesName}`;
    return metadata;
  };

  const setupViewer = async (metadata) => {
    if (!viewerRef.current || !mountedRef.current) {
      // Bailing out here used to leave the loading overlay spinning forever:
      // setLoading(false) only runs in the try/finally below. Surface it instead.
      if (mountedRef.current) {
        console.warn(`[RadiologyViewer ${instanceId}] Viewer element not attached; aborting setup`);
        setError("Le viewer n'a pas pu s'initialiser. Rechargez la page.");
        setLoading(false);
      }
      return;
    }

    try {
      viewportReadyRef.current = false;

      // Clean up existing rendering engine if needed
      if (renderingEngineRef.current) {
        await cleanup(false); // Don't do full cleanup, just this instance
      }

      const container = viewerRef.current;
      container.innerHTML = '';
      // Reset grid styles
      container.style.display = 'block';  // Changed from grid to block
      container.style.gridTemplateColumns = '';
      container.style.gridTemplateRows = '';
      container.style.gap = '';
      container.style.width = '100%';
      container.style.height = '100%';

      // Acquire the shared rendering engine only on first setup
      if (!renderingEngineRef.current) {
        renderingEngineRef.current = acquireRenderingEngine();
      }
      windowLevelSyncRef.current = new WindowLevelSyncManager(
        renderingEngineRef.current,
        toolGroupIdRef.current,
        handleVOIUpdate // Pass the callback here
      );

      // Set up instance-specific metadata provider
      // In both setupViewer and setupMPRView functions:
      const metadataProvider = (type, imageId) => {
        if (!metadata.has(imageId)) return;
        const meta = metadata.get(imageId);
        if (type === "imagePixelModule") {
          return {
            bitsAllocated: meta.bitsAllocated,
            bitsStored: meta.bitsStored,
            highBit: meta.highBit,
            pixelRepresentation: meta.pixelRepresentation,
            photometricInterpretation: meta.photometricInterpretation,
          };
        }
        if (type === "imagePlaneModule") {
          return {
            imageOrientationPatient: meta.imageOrientationPatient,
            imagePositionPatient: meta.imagePositionPatient,
            pixelSpacing: meta.pixelSpacing,
            rows: meta.rows,
            columns: meta.columns,
            frameOfReferenceUID: meta.frameOfReferenceUID || frameOfReferenceUIDRef.current,
          };
        }
      };

      cornerstone.metaData.addProvider(metadataProvider, instanceId);

      // Create and load volume
      const imageIds = Array.from(metadata.keys());
      // In both setupViewer and setupMPRView functions:
      const volumeLoadObject = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, {
        imageIds,
      });

      await volumeLoadObject.load();

      if (!mountedRef.current) return;

      // Enable the element
      await renderingEngineRef.current.enableElement({
        viewportId,
        element: viewerRef.current,
        type: cornerstone.Enums.ViewportType.ORTHOGRAPHIC,
        defaultOptions: {
          orientation: {
            viewPlaneNormal: [0, 0, -1], // Changed from [0, 0, 1] to invert x-axis
            viewUp: [0, -1, 0], // Reverted back to original
          },
          background: [0, 0, 0],
        },
      });

      const viewport = renderingEngineRef.current.getViewport(viewportId);
      
      if (!viewport) {
        throw new Error("Failed to create viewport");
      }

      viewport.setOptions({
        orientation: {
          viewPlaneNormal: [0, 0, -1], // Changed from [0, 0, 1] to invert x-axis
          viewUp: [0, -1, 0], // Reverted back to original
          preserveAspectRatio: true,
        },
      });

      await viewport.setVolumes([
        {
          volumeId,
          callback: ({ volumeActor }) => {
            volumeActor.getProperty().setInterpolationTypeToLinear();
          },
        },
      ]);

      const volume = cornerstone.cache.getVolume(volumeId);
      totalSlicesRef.current = volume.dimensions[2];
      const zSpacing = (volume.spacing && volume.spacing[2]) || 1;
      const maxSlab = Math.max(1, Math.round(volume.dimensions[2] * zSpacing));
      setSlabThicknessMax(maxSlab);

      // Register slice count for this instance
      sliceRegistry.set(instanceId, totalSlicesRef.current);
      
      // Update sync compatibility after setting slice count
      setSyncCompatible(checkSyncCompatibility());

      // Auto-sync with other exams that have the same number of slices
      if (checkSyncCompatibility() && totalSlicesRef.current > 1) {
        // Count how many other instances have the same slice count
        let sameSliceCountCount = 0;
        for (const otherId of activeInstances) {
          if (otherId !== instanceId && sliceRegistry.get(otherId) === totalSlicesRef.current) {
            sameSliceCountCount++;
          }
        }
        
        // If there are other instances with the same slice count, auto-enable sync
        if (sameSliceCountCount > 0) {
          
          // Prepare a custom event to synchronize all viewers with the same slice count
          const syncEvent = new CustomEvent('syncStateChanged', {
            detail: {
              sliceCount: totalSlicesRef.current,
              enabled: true,
              sourceInstanceId: instanceId
            }
          });
          
          // Update local state
          setSyncedState(true);
          
          // Apply synchronizer changes
          const synchronizer = getOrCreateCameraSynchronizer();
          synchronizer.add({ renderingEngineId, viewportId });
          
          // Add this slice count to active sync groups
          activeSyncedSliceCounts.add(totalSlicesRef.current);
          
          // Register slice change listener for this instance
          if (!sliceChangeListeners.has(totalSlicesRef.current)) {
            sliceChangeListeners.set(totalSlicesRef.current, new Map());
          }
          
          // Create a dedicated update function for this instance
          const syncUpdateFunction = (newSliceIndex) => {
            setCurrentSlice(newSliceIndex);
            
            // If viewport is ready, directly update the slice position
            if (viewportReadyRef.current && renderingEngineRef.current) {
              try {
                const viewport = renderingEngineRef.current.getViewport(viewportId);
                if (viewport) {
                  const volume = cornerstone.cache.getVolume(volumeId);
                  if (volume) {
                    // With X-axis inverted, use newSliceIndex directly
                    // Old calculation: const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                    const sliceIndex = newSliceIndex;
                    const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                    
                    if (typeof viewport.setImageIdIndex === 'function') {
                      viewport.setImageIdIndex(imageIndex);
                    } else if (typeof viewport.setDisplaySet === 'function') {
                      viewport.setDisplaySet(volumeId, { imageIndex });
                    }
                    viewport.render();
                  }
                }
              } catch (error) {
                console.warn(`[${instanceId}] Failed to update slice position:`, error);
              }
            }
          };
          
          sliceChangeListeners.get(totalSlicesRef.current).set(instanceId, syncUpdateFunction);
          
          // Dispatch the event to notify other viewers
          window.dispatchEvent(syncEvent);
        }
      }

      // Set the initial slice index with inverted X-axis
      const { currentStepIndex, numScrollSteps } =
        cornerstone.utilities.getVolumeViewportScrollInfo(viewport, volumeId);

      // With X-axis inverted, use currentStepIndex directly
      // Old calculation: setCurrentSlice(numScrollSteps - currentStepIndex);
      setCurrentSlice(currentStepIndex);

      const { voiRange } = viewport.getProperties();
      if (voiRange) {
        setVoiRange({
          windowWidth: voiRange.upper - voiRange.lower,
          windowCenter: (voiRange.upper + voiRange.lower) / 2,
        });
      }

      viewerRef.current.addEventListener(Events.MOUSE_WHEEL, handleSliceChange);
      windowLevelSyncRef.current.enable([viewportId], true);

      viewport.resetCamera();
      
      viewport.render();
      
    
      // Initialize tools and store the toolGroupId
      toolGroupIdRef.current = initializeTools();

      // Mark viewport as ready only after successful setup
      viewportReadyRef.current = true;


      const viewerElement = viewerRef.current;
        if (viewerElement) {
          viewerElement.style.cursor = "default";
        }

    } catch (error) {
      console.error(`Error in setupViewer for instance ${instanceId}:`, error);
      viewportReadyRef.current = false;
      throw error;
    }
  };

  const SHAPE_TO_TOOL = {
    rectangle: RectangleROITool.toolName,
    ellipse: EllipticalROITool.toolName,
    circle: CircleROITool.toolName,
    arrow: ArrowAnnotateTool.toolName,
    freehand: PlanarFreehandROITool.toolName,
  };
  const ANNOTATION_TOOL_NAMES = Object.values(SHAPE_TO_TOOL);
  const isAnnotating = ANNOTATION_TOOL_NAMES.includes(activeTool);

  const handleAnnotationShapePick = (shapeKey) => {
    const toolName = SHAPE_TO_TOOL[shapeKey];
    if (!toolName) return;
    setAnnotationShape(shapeKey);
    handleToolClick(toolName);
  };

  const handleToolClick = (toolName) => {
    const toolGroupId = `toolGroup-${instanceId}`;
    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

    if (!toolGroup) {
      console.error("Tool group not found");
      return;
    }
  
    const resetCursor = () => {
      if (isMPRView) {
        [axialViewportId, coronalViewportId, sagittalViewportId].forEach((viewportId) => {
          const viewportElement = renderingEngineRef.current.getViewport(viewportId)?.element;
          if (viewportElement) viewportElement.style.cursor = "default";
        });
      } else if (viewerRef.current) {
        viewerRef.current.style.cursor = "default";
      }
    };
  
    // Handle window level sync for MPR view
    if (isMPRView && windowLevelSyncRef.current) {
      if (toolName === WindowLevelTool.toolName) {
        const viewportIds = [axialViewportId, coronalViewportId, sagittalViewportId];
        windowLevelSyncRef.current.enable(viewportIds);
      } else {
        windowLevelSyncRef.current.disable();
      }
    }
  
    // First, set all tools to disabled state except StackScrollTool
    toolGroup.setToolDisabled(PanTool.toolName);
    toolGroup.setToolDisabled(ZoomTool.toolName);
    toolGroup.setToolEnabled(RectangleROITool.toolName);
    toolGroup.setToolEnabled(EllipticalROITool.toolName);
    toolGroup.setToolEnabled(CircleROITool.toolName);
    toolGroup.setToolEnabled(ArrowAnnotateTool.toolName);
    toolGroup.setToolEnabled(PlanarFreehandROITool.toolName);
    toolGroup.setToolDisabled(WindowLevelTool.toolName);
    if (isMPRView) {
      toolGroup.setToolDisabled(CrosshairsTool.toolName);
    }
  
    // Keep StackScrollTool always active with mouse wheel
    toolGroup.setToolActive(StackScrollTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Wheel }],
    });
  
    // Handle Crosshairs tool separately in MPR view
    if (toolName === "Crosshairs" && isMPRView) {
      const isActive = activeTool === "Crosshairs";
      if (isActive) {
        toolGroup.setToolDisabled(CrosshairsTool.toolName);
        setActiveToolState(null);
        // Re-enable the default tool (WindowLevel) when disabling crosshairs
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(WindowLevelTool.toolName);
        if (isMPRView && windowLevelSyncRef.current) {
          const viewportIds = [axialViewportId, coronalViewportId, sagittalViewportId];
          windowLevelSyncRef.current.enable(viewportIds);
        }
      } else {
        toolGroup.setToolActive(CrosshairsTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState("Crosshairs");
        if (windowLevelSyncRef.current) {
          windowLevelSyncRef.current.disable();
        }
      }
      resetCursor();
      return;
    }
  
    // Handle other tools
    if (toolName === activeTool) {
      // If clicking the active tool, disable it and activate the default tool (WindowLevel)
      toolGroup.setToolActive(WindowLevelTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
      setActiveToolState(WindowLevelTool.toolName);
      if (isMPRView && windowLevelSyncRef.current) {
        const viewportIds = [axialViewportId, coronalViewportId, sagittalViewportId];
        windowLevelSyncRef.current.enable(viewportIds);
      }
      resetCursor();
    } else {
      // Activate the clicked tool
      toolGroup.setToolActive(toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
      setActiveToolState(toolName);
    }

    // Remove the line that referenced undefined variables
  };
  
  
  
  
  
  const setupMPRView = async (metadata) => {
    if (!viewerRef.current || !mountedRef.current) return;
  
    try {
      viewportReadyRef.current = false;
  
      // Clean up existing rendering engine
      if (renderingEngineRef.current) {
        await cleanup(false);
      }
  
      // Acquire the shared rendering engine only on first setup
      if (!renderingEngineRef.current) {
        renderingEngineRef.current = acquireRenderingEngine();
      }
      windowLevelSyncRef.current = new WindowLevelSyncManager(
        renderingEngineRef.current,
        toolGroupIdRef.current,
        handleVOIUpdate // Pass the same callback (can handle both MPR and normal)
      );

  
      // Clear and set up the container for MPR views
      const container = viewerRef.current;
      container.innerHTML = '';
      
      // Create the MPR viewports
      const axialDiv = document.createElement('div');
      const coronalDiv = document.createElement('div');
      const sagittalDiv = document.createElement('div');
  
      // Style the container and viewports
      container.style.display = 'grid';
      container.style.gridTemplateColumns = '1fr 1fr';
      container.style.gridTemplateRows = '1fr 1fr';
      container.style.gap = '2px';
      container.style.height = '100%';
      container.style.width = '100%';
  
      // Style each viewport div
      [axialDiv, coronalDiv, sagittalDiv].forEach(div => {
        div.style.width = '100%';
        div.style.height = '100%';
        div.style.backgroundColor = 'black';
      });
  
      // Position the viewports in the grid
      axialDiv.style.gridColumn = '1 / 3';
      axialDiv.style.gridRow = '1 / 2';
      coronalDiv.style.gridColumn = '1 / 2';
      coronalDiv.style.gridRow = '2 / 3';
      sagittalDiv.style.gridColumn = '2 / 3';
      sagittalDiv.style.gridRow = '2 / 3';
  
      // Add viewports to container
      container.appendChild(axialDiv);
      container.appendChild(coronalDiv);
      container.appendChild(sagittalDiv);
  
      // Enable elements with proper orientations
      await renderingEngineRef.current.enableElement({
        viewportId: axialViewportId,
        type: cornerstone.Enums.ViewportType.ORTHOGRAPHIC,
        element: axialDiv,
        defaultOptions: {
          orientation: { viewPlaneNormal: [0, 0, -1], viewUp: [0, -1, 0] }, // Changed from [0, 0, 1] to invert x-axis
          background: [0, 0, 0],
        },
      });
  
      await renderingEngineRef.current.enableElement({
        viewportId: coronalViewportId,
        type: cornerstone.Enums.ViewportType.ORTHOGRAPHIC,
        element: coronalDiv,
        defaultOptions: {
          orientation: { viewPlaneNormal: [0, 1, 0], viewUp: [0, 0, 1] },
          background: [0, 0, 0],
        },
      });
  
      await renderingEngineRef.current.enableElement({
        viewportId: sagittalViewportId,
        type: cornerstone.Enums.ViewportType.ORTHOGRAPHIC,
        element: sagittalDiv,
        defaultOptions: {
          orientation: { viewPlaneNormal: [1, 0, 0], viewUp: [0, 0, 1] },
          background: [0, 0, 0],
        },
      });
  
      // Set up metadata provider
      const metadataProvider = (type, imageId) => {
        if (!metadata.has(imageId)) return;
        const meta = metadata.get(imageId);
        if (type === "imagePixelModule") {
          return {
            bitsAllocated: meta.bitsAllocated,
            bitsStored: meta.bitsStored,
            highBit: meta.highBit,
            pixelRepresentation: meta.pixelRepresentation,
            photometricInterpretation: meta.photometricInterpretation,
          };
        }
        if (type === "imagePlaneModule") {
          return {
            imageOrientationPatient: meta.imageOrientationPatient,
            imagePositionPatient: meta.imagePositionPatient,
            pixelSpacing: meta.pixelSpacing,
            rows: meta.rows,
            columns: meta.columns,
            frameOfReferenceUID: meta.frameOfReferenceUID || frameOfReferenceUIDRef.current,
          };
        }
      };

      cornerstone.metaData.addProvider(metadataProvider, instanceId);

      // Create and load volume
      const imageIds = Array.from(metadata.keys());
      const volumeLoadObject = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, {
        imageIds,
      });
  
      await volumeLoadObject.load();
  
      // Set up each viewport
      const viewportIds = [axialViewportId, coronalViewportId, sagittalViewportId];
      for (const viewportId of viewportIds) {
        const viewport = renderingEngineRef.current.getViewport(viewportId);
        
        await viewport.setVolumes([
          {
            volumeId,
            callback: ({ volumeActor }) => {
              volumeActor.getProperty().setInterpolationTypeToLinear();
            },
          },
        ]);

  
        viewport.resetCamera();
        viewport.render();
      }

      
      
      windowLevelSyncRef.current.enable(viewportIds);
      // Initialize tools
      initializeMPRTools();
      
  
      viewportReadyRef.current = true;
    } catch (error) {
      console.error(`Error in setupMPRView:`, error);
      viewportReadyRef.current = false;
      throw error;
    }
  };

  
  // Add this function for MPR tools initialization
  const initializeMPRTools = () => {
    const toolGroupId = `toolGroup-${instanceId}`;
    let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    
    if (toolGroup) {
      ToolGroupManager.destroyToolGroup(toolGroupId);
    }
  
    toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
    
    // Add tools to the tool group
    toolGroup.addTool(PanTool.toolName);
    toolGroup.addTool(ZoomTool.toolName);
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.addTool(CrosshairsTool.toolName);
    toolGroup.addTool(WindowLevelTool.toolName);
  
    // Set up tool modes
    toolGroup.setToolActive(StackScrollTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Wheel }],
    });

    toolGroup.setToolActive(WindowLevelTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });

    // Add viewports to tool group
    [axialViewportId, coronalViewportId, sagittalViewportId].forEach(viewportId => {
      toolGroup.addViewport(viewportId, renderingEngineId);
    });
  
    return toolGroupId;
  };


  const handleMprClick = async () => {
    setLoading(true);
    const wasSynced = isSynced; // Capture sync state *before* the change
    const oldViewportIds = isMPRView
      ? [axialViewportId, coronalViewportId, sagittalViewportId]
      : [viewportId];

    // If synced, remove current viewports from synchronizer *before* changing view
    if (wasSynced) {
        const synchronizer = getOrCreateCameraSynchronizer();
        oldViewportIds.forEach(vpId => {
             try {
               synchronizer.remove({ renderingEngineId, viewportId: vpId });
             } catch(e) { /* ignore */ }
        });
    }

    try {
      // Get tool group before potential cleanup/reset
      const toolGroupId = `toolGroup-${instanceId}`;
      const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

      if (!toolGroup) {
        console.error("Tool group not found");
        // If no tool group, can we proceed? Probably not safely.
        setLoading(false); // Ensure loading stops
        return;
      }

      const resetCursor = () => {
        const targetViewportIds = isMPRView // Use the *current* state before toggle
          ? [axialViewportId, coronalViewportId, sagittalViewportId]
          : [viewportId];
        targetViewportIds.forEach((vpId) => {
            try {
                const viewport = renderingEngineRef.current?.getViewport(vpId);
                if (viewport?.element) viewport.element.style.cursor = "default";
            } catch(e) { /* ignore lookup errors */ }
        });
      };


      // Deactivate the Crosshairs tool if it is active
      if (activeTool === "Crosshairs" && isMPRView) { // Check isMPRView state *before* toggle
        toolGroup.setToolDisabled(CrosshairsTool.toolName);
        setActiveToolState(null); // Reset active tool state
        // We might need to reactivate the default tool (WindowLevel) here if Crosshairs was primary
        toolGroup.setToolActive(WindowLevelTool.toolName, {
           bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(WindowLevelTool.toolName); // Reflect this change
        resetCursor(); // Reset cursor after tool changes
      }


      // Perform the view switch
      const nextIsMPRView = !isMPRView;
      if (nextIsMPRView) {
         await setupMPRView(metadataRef.current);
         // Cursor reset for MPR viewports should ideally happen within setupMPRView or after it's confirmed ready
      } else {
         await setupViewer(metadataRef.current);
         const viewerElement = viewerRef.current;
         if (viewerElement) viewerElement.style.cursor = "default";
      }
      setIsMPRView(nextIsMPRView); // Update MPR state *after* setup is complete

       // If it was synced, add the *new* viewports *after* the view has changed and state updated
      if (wasSynced) {
          const synchronizer = getOrCreateCameraSynchronizer();
          const newViewportIds = nextIsMPRView // Use the *new* state here
              ? [axialViewportId, coronalViewportId, sagittalViewportId]
              : [viewportId];
           newViewportIds.forEach(vpId => {
               try { // Add try-catch for robustness
                const viewport = renderingEngineRef.current?.getViewport(vpId);
                const hasVolume = viewport?.getVolumes()?.length > 0;
                
                if (viewport && hasVolume) {
                  synchronizer.add({ renderingEngineId, viewportId: vpId });
                  viewport.resetCamera(); // recale après synchro
                  viewport.render();
                } else {
                  console.warn(`[Sync] Viewport ${vpId} has no volume, skipping sync.`);
                }
                
               } catch(e) {
                   console.error(`Failed to add viewport ${vpId} to synchronizer:`, e);
               }
           });
      }

    } catch (error) {
      console.error("Error switching MPR view:", error);
      setError("Failed to switch view mode");
       // If an error occurred during view switch, sync state might be inconsistent with actual synchronizer membership.
       // Consider adding logic here to ensure consistency if needed, e.g., re-querying synchronizer state.
    } finally {
      setLoading(false);
    }
  };
  
  

  const cleanup = async (isFullCleanup = true) => {
    try {
      viewportReadyRef.current = false;

       // If synced, remove from synchronizer during cleanup
       if (isSynced) {
           const synchronizer = SynchronizerManager.getSynchronizer(CAMERA_SYNCHRONIZER_ID);
           if (synchronizer) {
               const currentViewportIds = isMPRView
                 ? [axialViewportId, coronalViewportId, sagittalViewportId]
                 : [viewportId];
                currentViewportIds.forEach(vpId => {
                    try {
                       synchronizer.remove({ renderingEngineId, viewportId: vpId });
                    } catch(e) {
                        // console.warn(`Minor error removing viewport ${vpId} during cleanup:`, e);
                         /* ignore if viewport already removed or doesn't exist */
                    }
                });

                 // Optional: Check if synchronizer is now empty and destroy?
                 // Avoid destroying if it might be used by other non-cleaned-up instances.
                 // This requires more complex global management.
           }
       }

      if (windowLevelSyncRef.current) {
        windowLevelSyncRef.current.cleanup();
        windowLevelSyncRef.current = null;
      }

      viewerRef.current?.removeEventListener(Events.MOUSE_WHEEL, handleSliceChange);
      
      if (renderingEngineRef.current) {
        const viewportIds = isMPRView 
          ? [axialViewportId, coronalViewportId, sagittalViewportId]
          : [viewportId];
  
        for (const vpId of viewportIds) {
          try {
            const viewport = renderingEngineRef.current.getViewport(vpId);
            if (viewport) {
              viewport.reset();
              viewport.dispose();
            }
          } catch (e) {
            console.warn(`Error cleaning up viewport ${vpId}:`, e);
          }
  
          try {
            renderingEngineRef.current.disableElement(vpId);
          } catch (e) {
            console.warn(`Error disabling element ${vpId}:`, e);
          }
        }
  
        if (isFullCleanup) {
          try {
            releaseRenderingEngine();
          } catch (e) {
            console.warn(`Error releasing shared rendering engine:`, e);
          }
          renderingEngineRef.current = null;
        }
      }

      // Remove instance-specific volume from cache
      try {
        cornerstone.cache.removeVolumeLoadObject(volumeId);
      } catch (e) {
        console.warn(`Error removing volume from cache for instance ${instanceId}:`, e);
      }

      // Remove instance-specific metadata provider
      try {
        cornerstone.metaData.removeProvider(instanceId);
      } catch (e) {
        console.warn(`Error removing metadata provider for instance ${instanceId}:`, e);
      }

      // Clean up tool group - this should already remove viewports, but sync removal is explicit
    if (toolGroupIdRef.current) {
      try {
        const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
        if (toolGroup) {
          toolGroup.removeViewport(viewportId, renderingEngineId);
          ToolGroupManager.destroyToolGroup(toolGroupIdRef.current);
        }
      } catch (e) {
        console.warn(`Error cleaning up tool group for instance ${instanceId}:`, e);
      }
    }

      if (isFullCleanup && activeInstances.size === 1 && activeInstances.has(instanceId)) {
        // Only reset global state if this is the last instance
        globalInitPromise = null;
        try {
          cornerstone.cache.purgeCache();
        } catch (e) {
          console.warn('Error purging global cache:', e);
        }
        revokeDicomZipCache(seriesName, process.env.PUBLIC_URL || "", zipUrl);
      }
    } catch (err) {
      console.warn(`Cleanup error for instance ${instanceId}:`, err);
    }
  };

  // Update the setIsSynced call to also update the global sync state
  const setSyncedState = (newState) => {
    // Update the global state immediately for synchronous access
    globalSyncState[instanceId] = typeof newState === 'function' 
      ? newState(globalSyncState[instanceId] || false) 
      : newState;
    
    // Then update the React state
    setIsSynced(newState);
  };

  // Update the handleResize function to handle both single and MPR views
const handleResize = () => {
  if (!mountedRef.current || !viewportReadyRef.current) {
    return;
  }

  try {
    const renderingEngine = renderingEngineRef.current;
    if (!renderingEngine) {
      console.warn(`[ResizeObserver] Rendering engine not available for ${instanceId}`);
      return;
    }

    // Check if the element has actual dimensions
    const element = viewerRef.current;
    if (!element || element.offsetWidth === 0 || element.offsetHeight === 0) {
      console.warn(`[ResizeObserver] Element has zero dimensions for ${instanceId}`);
      return;
    }

    // Get relevant viewport IDs based on current view mode
    const viewportIds = isMPRView 
      ? [axialViewportId, coronalViewportId, sagittalViewportId]
      : [viewportId];

    // Store current image indices directly from viewports before resize
    const imageIndices = {};
    for (const vpId of viewportIds) {
      try {
        const viewport = renderingEngine.getViewport(vpId);
        if (viewport && typeof viewport.getCurrentImageIdIndex === 'function') {
          imageIndices[vpId] = viewport.getCurrentImageIdIndex();
        }
      } catch (e) {
        console.warn(`[ResizeObserver] Failed to get image index for viewport ${vpId}:`, e);
      }
    }

    // If we couldn't get indices directly, try to use the slice number for the main viewport
    if (Object.keys(imageIndices).length === 0 && currentSlice !== undefined) {
      try {
        const volume = cornerstone.cache.getVolume(volumeId);
        if (volume) {
          const numSlices = volume.dimensions[2];
          // With X-axis inverted, use currentSlice directly
          // Old calculation before X-axis inversion:
          // const internalIndex = numSlices - currentSlice;
          // const cornerstoneIndex = Math.max(0, Math.min(internalIndex - 1, numSlices - 1));
          const cornerstoneIndex = Math.max(0, Math.min(currentSlice, numSlices - 1));
          imageIndices[viewportId] = cornerstoneIndex;
        }
      } catch (e) {
        console.warn(`[ResizeObserver] Failed to calculate fallback index:`, e);
      }
    }

    // Snapshot each viewport's camera (zoom/pan) so a resize doesn't reset it.
    const savedCameras = {};
    for (const vpId of viewportIds) {
      try {
        const vp = renderingEngine.getViewport(vpId);
        if (vp) savedCameras[vpId] = vp.getCamera();
      } catch (e) {
        /* ignore */
      }
    }

    // Resize the rendering engine
    renderingEngine.resize(true, false);

    // Then handle each viewport
    for (const vpId of viewportIds) {
      try {
        const viewport = renderingEngine.getViewport(vpId);
        if (viewport) {
          // Restore the pre-resize camera to preserve zoom/pan; fall back to a
          // gentle reset only if no snapshot was captured.
          if (savedCameras[vpId]) {
            viewport.setCamera(savedCameras[vpId]);
          } else {
            viewport.resetCamera(false);
          }

          // Restore the exact image index we had before
          if (imageIndices[vpId] !== undefined) {
            
            // Try different methods to set the image index
            try {
              if (typeof viewport.setImageIdIndex === 'function') {
                viewport.setImageIdIndex(imageIndices[vpId]);
              } else if (typeof viewport.setDisplaySet === 'function') {
                viewport.setDisplaySet(volumeId, { imageIndex: imageIndices[vpId] });
              } else {
                // Direct approach using jump utility if available
                try {
                  if (viewport.element && cornerstone.utilities && 
                      typeof cornerstone.utilities.jumpToSlice === 'function') {
                    cornerstone.utilities.jumpToSlice(viewport.element, {
                      imageIndex: imageIndices[vpId]
                    });
                  }
                } catch (jumpError) {
                  console.warn(`[ResizeObserver] Jump to slice failed: ${jumpError}`);
                }
              }
            } catch (indexError) {
              console.warn(`[ResizeObserver] Setting image index failed: ${indexError}`);
            }
          }
          
          viewport.render();
        }
      } catch (e) {
        console.warn(`[ResizeObserver] Failed to resize viewport ${vpId}:`, e);
      }
    }

  } catch (err) {
    console.warn(`[ResizeObserver] Error during resize for ${instanceId}:`, err);
  }
};

  useEffect(() => {
    if (!viewerRef.current) {
      console.warn(`[ResizeObserver] viewerRef.current is not attached for ${instanceId}`);
      return;
    }

    let resizeTimeout;
    const observer = new ResizeObserver(() => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        handleResize();
      }, 250);
    });

    observer.observe(viewerRef.current);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      observer.disconnect();
    };
  }, [viewportId, renderingEngineRef.current, volumeId, currentSlice, instanceId, isMPRView, targetAnnotationId, annotations]); // Add dependencies needed by handleResize

  useEffect(() => {
    setLoading(true);
    setError(null);

    const setup = async () => {
      try {
        if (!mountedRef.current) return;

        await initializeCornerstone();
        if (!mountedRef.current) return;

        const metadata = await loadMetadata();
        if (!mountedRef.current) return;

        metadataRef.current = metadata;

        await setupViewer(metadata);

        if (!mountedRef.current) return;
        setLoading(false);
      } catch (err) {
        console.error(`Setup error for instance ${instanceId}:`, err);
        if (mountedRef.current) {
          setError(err.message || "Failed to setup viewer");
          setLoading(false);
        }
      }
    };

    setup();

    return () => {
      mountedRef.current = false;
      cleanup(true);
    };
  }, [seriesName]);

  // Add this new useEffect to log sync state after mount


  // Ensure sync is properly set up when viewport is ready
  useEffect(() => {
    if (viewportReadyRef.current && isSynced && totalSlicesRef.current > 1) {

      
      // Set global sync state to match React state
      globalSyncState[instanceId] = true;
      
      // Re-register with synchronizer if needed
      const currentSliceCount = totalSlicesRef.current;
      if (!sliceChangeListeners.has(currentSliceCount)) {
        sliceChangeListeners.set(currentSliceCount, new Map());
      }
      
      // Re-register our callback if it's not already there
      if (!sliceChangeListeners.get(currentSliceCount).has(instanceId)) {

        const syncUpdateFunction = (newSliceIndex) => {

          setCurrentSlice(newSliceIndex);
          
          // Direct viewport update code (same as before)
          if (viewportReadyRef.current && renderingEngineRef.current) {
            try {
              const viewport = renderingEngineRef.current.getViewport(viewportId);
              if (viewport) {
                const volume = cornerstone.cache.getVolume(volumeId);
                if (volume) {
                  const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                  const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                  
                  if (typeof viewport.setImageIdIndex === 'function') {
                    viewport.setImageIdIndex(imageIndex);
                  } else if (typeof viewport.setDisplaySet === 'function') {
                    viewport.setDisplaySet(volumeId, { imageIndex });
                  }
                  viewport.render();
                }
              }
            } catch (error) {
              console.warn(`[${instanceId}] Failed to update slice:`, error);
            }
          }
        };
        
        sliceChangeListeners.get(currentSliceCount).set(instanceId, syncUpdateFunction);
        activeSyncedSliceCounts.add(currentSliceCount);
        
        // Force a sync group change notification
        window.dispatchEvent(new Event('syncGroupsChanged'));
      }
    }
  }, [viewportReadyRef.current, isSynced, totalSlicesRef.current, instanceId, viewportId, volumeId]);

  // Add listener for sync state changes from other viewers
  useEffect(() => {
    const handleSyncStateChange = (event) => {
      const { sliceCount, enabled, sourceInstanceId } = event.detail;
      
      // Only respond if this is a different instance with the same slice count
      if (sourceInstanceId !== instanceId && totalSlicesRef.current === sliceCount) {

        
        // Only update if the sync state is different from our current state
        // This prevents unnecessary updates when multiple exams are loaded at once
        const currentGlobalSyncState = globalSyncState[instanceId] !== undefined 
          ? globalSyncState[instanceId] 
          : isSynced;
          
        if (currentGlobalSyncState !== enabled) {
          // Force a state update with a small delay to ensure it processes after any pending state changes
          setTimeout(() => {
            setSyncedState(enabled);
          }, 50);
          
          // If enabling sync, ensure we're registered
          if (enabled) {
            const synchronizer = getOrCreateCameraSynchronizer();
            
            try {
              synchronizer.add({ renderingEngineId, viewportId });
              
              // Make sure we're registered as a listener
              if (!sliceChangeListeners.has(sliceCount)) {
                sliceChangeListeners.set(sliceCount, new Map());
              }
              
              // Create a callback function if we don't already have one
              if (!sliceChangeListeners.get(sliceCount).has(instanceId)) {
                const syncUpdateFunction = (newSliceIndex) => {
                  setCurrentSlice(newSliceIndex);
                  
                  // Direct viewport update
                  if (viewportReadyRef.current && renderingEngineRef.current) {
                    try {
                      const viewport = renderingEngineRef.current.getViewport(viewportId);
                      if (viewport) {
                        const volume = cornerstone.cache.getVolume(volumeId);
                        if (volume) {
                          const sliceIndex = volume.dimensions[2] - 1 - newSliceIndex;
                          const imageIndex = Math.max(0, Math.min(sliceIndex, volume.dimensions[2] - 1));
                          
                          if (typeof viewport.setImageIdIndex === 'function') {
                            viewport.setImageIdIndex(imageIndex);
                          } else if (typeof viewport.setDisplaySet === 'function') {
                            viewport.setDisplaySet(volumeId, { imageIndex });
                          }
                          viewport.render();
                        }
                      }
                    } catch (error) {
                      console.warn(`[${instanceId}] Failed to directly update slice position:`, error);
                    }
                  }
                };
                
                sliceChangeListeners.get(sliceCount).set(instanceId, syncUpdateFunction);
              }
            } catch (error) {
              console.error(`[${instanceId}] Error syncing with external request:`, error);
            }
          } else {
            // If disabling sync, remove from synchronizer
            const synchronizer = SynchronizerManager.getSynchronizer(CAMERA_SYNCHRONIZER_ID);
            if (synchronizer) {
              try {
                synchronizer.remove({ renderingEngineId, viewportId });
              } catch (error) {
                console.warn(`[${instanceId}] Error removing from synchronizer:`, error);
              }
            }
            
            // Remove from listeners
            const listeners = sliceChangeListeners.get(sliceCount);
            if (listeners && listeners.has(instanceId)) {
              listeners.delete(instanceId);
            }
          }
        }
      }
    };
    
    // Add event listener
    window.addEventListener('syncStateChanged', handleSyncStateChange);
    
    return () => {
      window.removeEventListener('syncStateChanged', handleSyncStateChange);
    };
  }, [isSynced, instanceId, renderingEngineId, totalSlicesRef, viewportId, volumeId]);

  // --- Annotation Rendering Effect ---
  useEffect(() => {
    if (!showAnnotations || !viewportReadyRef.current || !renderingEngineRef.current) {
      return;
    }

    const renderingEngine = renderingEngineRef.current;

    const relevantViewportIds = isMPRView
      ? [axialViewportId, coronalViewportId, sagittalViewportId]
      : [viewportId];

    const primaryViewportId = relevantViewportIds[0];
    const viewport = renderingEngine.getViewport(primaryViewportId);

    if (!viewport) {
      console.warn(`[RadiologyViewer ${instanceId}] Primary viewport (${primaryViewportId}) not found for annotation processing.`);
      return;
    }

    // Use the viewport's own FoRUID as the authoritative value — avoids mismatch
    // when the DICOM tag is absent or differs from Cornerstone's derived value.
    const currentFoRUID = viewport.getFrameOfReferenceUID() || frameOfReferenceUIDRef.current;
    if (!currentFoRUID) {
      console.warn(`[RadiologyViewer ${instanceId}] No FrameOfReferenceUID available, skipping annotation update.`);
      return;
    }


    try {

      // --- Annotation Cleanup ---
      // Get all annotations currently in the state for this FoR
      const existingAnnotationsInState = annotation.state.getAllAnnotations().filter(anno => 
        anno.metadata?.FrameOfReferenceUID === currentFoRUID
      );
      const annotationsFromPropIds = new Set(annotations.map(a => a.id));

      // Find annotations in the state that are NO LONGER in the props
      const annotationsToRemove = existingAnnotationsInState.filter(
        stateAnno => !annotationsFromPropIds.has(stateAnno.annotationUID)
      );

      if (annotationsToRemove.length > 0) {
          annotationsToRemove.forEach(annoToRemove => {
            try {
              annotation.state.removeAnnotation(annoToRemove.annotationUID);
            } catch (error) {
              console.warn(`[RadiologyViewer ${instanceId}] Error removing annotation ${annoToRemove.annotationUID}:`, error);
            }
          });
      }


      // --- Add/Update Annotations ---
      const allImageIds = Array.from(metadataRef.current.keys());
      let addedOrUpdatedCount = 0;

      annotations.forEach((anno, index) => {
        // Basic validation
        if (!anno.toolName || !anno.points || !anno.id || typeof anno.sliceIndex !== 'number') {
          console.warn(`[RadiologyViewer ${instanceId}] Skipping annotation due to missing/invalid properties (toolName, points, id, sliceIndex):`, anno);
          return;
        }

        // Find the correct imageId for the slice
        // IMPORTANT: Assumes allImageIds is sorted correctly and anno.sliceIndex corresponds to the index in this array.
        // If sliceIndex represents something else (e.g., distance), this needs adjustment.
        const cornerstoneSliceIndex = anno.sliceIndex; // Assuming anno.sliceIndex is the 0-based cornerstone index
        if (cornerstoneSliceIndex < 0 || cornerstoneSliceIndex >= allImageIds.length) {
            console.warn(`[RadiologyViewer ${instanceId}] Skipping annotation ${anno.id} due to invalid sliceIndex (${cornerstoneSliceIndex}) relative to available image IDs (${allImageIds.length}).`);
            return;
        }
        const imageId = allImageIds[cornerstoneSliceIndex];
        if (!imageId) {
           console.warn(`[RadiologyViewer ${instanceId}] Could not find imageId for sliceIndex ${cornerstoneSliceIndex}. Skipping annotation ${anno.id}.`);
           return;
        }

        // --- Get correct world Z for this slice ---
        const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId);
        if (!imagePlaneModule || !imagePlaneModule.imagePositionPatient) {
            console.warn(`[RadiologyViewer ${instanceId}] Missing imagePlaneModule or imagePositionPatient for imageId ${imageId}. Cannot determine world Z for annotation ${anno.id}.`);
            return; // Skip this annotation if we can't get the Z coordinate
        }
        
        const worldZ = imagePlaneModule.imagePositionPatient[2];



        try {
          // Process the annotation points using original X, Y but correct world Z
          let originalPointsWithZ = anno.points;

          // Ensure we have valid points array
          if (!Array.isArray(originalPointsWithZ) || originalPointsWithZ.length < 2) {
            console.warn(`[RadiologyViewer ${instanceId}] Invalid points for annotation ${anno.id}:`, originalPointsWithZ);
            return;
          }

          // The stored point Z is authoritative (captured from Cornerstone at
          // draw time). Only substitute the index-derived worldZ when a point
          // has no usable Z — the index→imageId mapping (instanceNumber order)
          // can disagree with the volume's spatial order on some series, which
          // used to shift annotations onto the wrong slice.
          const correctedPoints = originalPointsWithZ.map(point =>
            Number.isFinite(point[2]) ? point : [point[0], point[1], worldZ]
          );
          const planeZ = correctedPoints[0][2];

          const annoToolName = anno.toolName || 'RectangleROI';

          // RectangleROI persists 2 opposite corners; reconstruct 4-handle order.
          // Other tools persist the full handles.points array as drawn.
          let handlesPoints;
          if (annoToolName === 'RectangleROI' && correctedPoints.length === 2) {
            const topLeft = correctedPoints[0];
            const bottomRight = correctedPoints[1];
            const topRight = [bottomRight[0], topLeft[1], planeZ];
            const bottomLeft = [topLeft[0], bottomRight[1], planeZ];
            handlesPoints = [topLeft, topRight, bottomLeft, bottomRight];
          } else {
            handlesPoints = correctedPoints;
          }

          const annoData = {
            handles: {
              points: handlesPoints,
              activeHandleIndex: null,
            },
            label: '',
            text: '',
            cachedStats: {
              [`volumeId:${volumeId}?sliceIndex=${cornerstoneSliceIndex}`]: {}
            },
            color: rgbArrayForAnno(anno),
            lineWidth: 3,
            isVisible: true
          };

          // PlanarFreehandROI renders from data.contour.polyline, not handles —
          // without it Cornerstone throws while rendering. Provide the contour.
          if (annoToolName === 'PlanarFreehandROI') {
            annoData.contour = { polyline: correctedPoints, closed: false };
          }

          const cornerstoneAnnotation = {
            annotationUID: anno.id,
            metadata: {
              toolName: annoToolName,
              referencedImageId: imageId,
              FrameOfReferenceUID: currentFoRUID,
            },
            data: annoData,
            highlighted: true,
            isLocked: true,
            isVisible: true,
          };

          // Create the annotation group selector
          const annotationGroupSelector = {
            viewportUID: viewportId,
            FrameOfReferenceUID: currentFoRUID
          };

          // Log the annotation object being added

          // Add or update the annotation in the state using the correct selector format
          annotation.state.addAnnotation(cornerstoneAnnotation, annotationGroupSelector);
          addedOrUpdatedCount++;
          
          // Apply style to make sure the annotation is visible and properly styled
          const style = {
            textBoxVisibility: false,
            colorHighlighted: colorForAnno(anno),
            color: colorForAnno(anno),
            isVisible: true,
            lineWidth: 3
          };
          annotation.config.style.setAnnotationStyles(anno.id, style);

          // Log successful addition with more details when an annotation is targeted
          

        } catch (error) {
          console.error(
            `[RadiologyViewer ${instanceId}] Failed to process or add annotation ${anno.id}:`,
            error,
            anno // Log the original annotation data for debugging
          );
        }
      });


      // Trigger render for relevant viewports
      if (renderingEngine) {
        renderingEngine.renderViewports(relevantViewportIds);
      }

    } catch (error) {
      console.error(`[RadiologyViewer ${instanceId}] Error processing annotations:`, error);
    }

    return () => {};

  }, [
      annotations,
      showAnnotations,
      viewportReadyRef.current,
      isMPRView,
      instanceId,
      targetAnnotationId,
      axialViewportId,
      coronalViewportId,
      sagittalViewportId,
      viewportId,
      itemId
     ]);
     
  // --- Handle Annotation Visibility Changes Effect ---
  useEffect(() => {
    // Skip if viewport isn't ready or we don't have annotations
    if (!viewportReadyRef.current || !renderingEngineRef.current || !annotations.length) {
      return;
    }

    
    try {
      // Get all annotations in the state
      const allAnnotations = annotation.state.getAllAnnotations();
      
      // Process all annotations that match our prop IDs
      const annotationIds = new Set(annotations.map(a => a.id));
      
      allAnnotations.forEach(anno => {
        if (annotationIds.has(anno.annotationUID)) {
          // Set visibility using the correct method
          annotation.visibility.setAnnotationVisibility(anno.annotationUID, showAnnotations);
        }
      });
      
      // Render the relevant viewports to show/hide annotations
      const relevantViewportIds = isMPRView
        ? [axialViewportId, coronalViewportId, sagittalViewportId]
        : [viewportId];
        
      renderingEngineRef.current.renderViewports(relevantViewportIds);
    } catch (error) {
      console.error(`[RadiologyViewer ${instanceId}] Error setting annotation visibility:`, error);
    }
  }, [showAnnotations, annotations, instanceId, viewportId, axialViewportId, coronalViewportId, sagittalViewportId, isMPRView, itemId]);
  
  // Inside the RadiologyViewer component, add this effect
  useEffect(() => {
    // Add a global mousedown handler to track which viewport user is interacting with
    const handleGlobalMouseDown = (e) => {
      // Check if mouse is within this viewer's boundaries
      if (viewerRef.current && viewerRef.current.contains(e.target)) {
        window.ACTIVE_RADIOLOGY_VIEWPORT = {
          viewportId,
          itemId,
          instanceId
        };
      }
    };
    
    document.addEventListener('mousedown', handleGlobalMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown, true);
    };
  }, [instanceId, itemId, viewportId]);

  const applyMipState = useCallback((enabled, thicknessMm) => {
    const renderingEngine = renderingEngineRef.current;
    if (!renderingEngine) return;
    try {
      const viewport = renderingEngine.getViewport(viewportId);
      if (!viewport || typeof viewport.setBlendMode !== 'function') return;
      const blendMode = enabled
        ? cornerstone.Enums.BlendModes.MAXIMUM_INTENSITY_BLEND
        : cornerstone.Enums.BlendModes.COMPOSITE;
      viewport.setBlendMode(blendMode);
      if (typeof viewport.setSlabThickness === 'function') {
        viewport.setSlabThickness(enabled ? thicknessMm : 0);
      }
      viewport.render();
    } catch (error) {
      console.error(`[RadiologyViewer ${instanceId}] Failed to apply MIP state:`, error);
    }
  }, [viewportId, instanceId]);

  const handleMipToggle = useCallback(() => {
    setIsMip((prev) => {
      const next = !prev;
      applyMipState(next, slabThickness);
      return next;
    });
  }, [applyMipState, slabThickness]);

  const handleSlabThicknessChange = useCallback((value) => {
    setSlabThickness(value);
    if (isMip) applyMipState(true, value);
  }, [applyMipState, isMip]);

  const handleScrollbarChange = useCallback((targetIndex) => {
    if (isMPRView) return;
    const renderingEngine = renderingEngineRef.current;
    if (!renderingEngine) return;
    const viewport = renderingEngine.getViewport(viewportId);
    const volume = cornerstone.cache.getVolume(volumeId);
    if (!viewport || !volume || !viewport.element) return;
    const numSlices = volume.dimensions[2];
    const cornerstoneIndex = Math.max(0, Math.min(targetIndex, numSlices - 1));
    csCoreUtilities.jumpToSlice(viewport.element, { imageIndex: cornerstoneIndex })
      .then(() => {
        const { currentStepIndex } = cornerstone.utilities.getVolumeViewportScrollInfo(viewport, volumeId);
        setCurrentSlice(currentStepIndex);
        if (isSynced) updateSyncedSliceCounters(currentStepIndex);
        viewport.render();
      })
      .catch((error) => {
        console.error(`[RadiologyViewer ${instanceId}] Scrollbar jumpToSlice failed for ${cornerstoneIndex}:`, error);
      });
  }, [isMPRView, viewportId, volumeId, isSynced, updateSyncedSliceCounters, instanceId]);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%", backgroundColor: "black" }}>
      {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent black background
              zIndex: 1000, // Ensure it appears above other elements
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Spin size="large" style={{ color: "#3498db" }} />
              <div style={{ fontSize: 12, color: "#f0f0f0" }}>
                {formatLoadProgress(loadProgress)}
              </div>
            </div>
          </div>
        )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white">
          Error: {error}
        </div>
      )}
      <div
        ref={viewerRef}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "black",
          position: "absolute",
        }}
      />
      <RadiologyToolbar
        activeTool={activeTool}
        onToolClick={handleToolClick}
        isMPRView={isMPRView}
        isSynced={isSynced}
        syncCompatible={syncCompatible}
        totalSlices={totalSlicesRef.current}
        onSyncToggle={handleSyncToggle}
        onMprClick={handleMprClick}
        crosshairsToolName={CrosshairsTool.toolName}
        isFullscreen={isFullscreen}
        onFullscreenToggle={handleFullscreenToggle}
        isMip={isMip}
        onMipToggle={handleMipToggle}
        slabThickness={slabThickness}
        slabThicknessMax={slabThicknessMax}
        onSlabThicknessChange={handleSlabThicknessChange}
        annotationShape={annotationShape}
        isAnnotating={isAnnotating}
        onAnnotationShapePick={handleAnnotationShapePick}
        showAnnotationPicker={true}
        syncTooltipText={
          totalSlicesRef.current <= 1
            ? "Exam cannot be synced (single slice)"
            : !syncCompatible && activeSyncedSliceCounts.size > 0
            ? `This exam (${totalSlicesRef.current} slices) cannot be synced with currently synced exams (${Array.from(activeSyncedSliceCounts).join(", ")} slices)`
            : (isSynced ? "Disable Exam Sync" : "Enable Exam Sync")
        }
        style={{
          position: "absolute",
          top: "15px",
          left: "15px",
          zIndex: 10,
        }}
      />
      {!isMPRView && (
        <RadiologyViewerInfo
          sliceIndex={currentSlice}
          totalSlices={totalSlicesRef.current}
          windowWidth={voiRange.windowWidth}
          windowCenter={voiRange.windowCenter}
        />
      )}
      {!isMPRView && (
        <SliceScrollbar
          currentSlice={currentSlice}
          totalSlices={totalSlicesRef.current}
          onSliceChange={handleScrollbarChange}
        />
      )}

    </div>
  );
};

export default RadiologyViewer;